import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Sparkles, Star, MessageSquare, Music2, X } from 'lucide-react';
import { songs, specialRequests } from '../data/songList';

const BOOKING_PHONE = '209-424-9048';

const SongRequestsPage = () => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return songs.map((s, i) => ({ song: s, index: i + 1 }));
    return songs
      .map((s, i) => ({ song: s, index: i + 1 }))
      .filter(({ song }) => song.toLowerCase().includes(q));
  }, [query]);

  const smsHref = (song) => {
    const body = encodeURIComponent(`Song request: ${song}`);
    const phone = BOOKING_PHONE.replace(/-/g, '');
    return `sms:+1${phone}?&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="song-requests-page">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[400px] h-[400px] rounded-full bg-pink-500/10 blur-3xl" />
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to="/live"
            className="inline-flex items-center gap-2 text-sm sm:text-base text-gray-200 hover:text-purple-300 transition-colors"
            data-testid="back-to-live"
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </Link>
          <h1 className="text-base sm:text-xl font-bold gradient-text">SONG REQUESTS</h1>
          <div className="w-10" />
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        {/* Intro */}
        <div className="text-center mb-6 sm:mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <Music2 size={14} />
            APPROXIMATE SONG LIST
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text mb-2">Pick a song</h2>
          <p className="text-sm sm:text-base text-gray-300 max-w-lg mx-auto">
            Tap any song to text it to me, or just shout it out from the crowd.
          </p>
        </div>

        {/* Pinned / special requests */}
        <div className="space-y-3 mb-6" data-testid="special-requests">
          {specialRequests.map((req, i) => (
            <a
              key={i}
              href={smsHref(req)}
              className="flex items-center gap-3 rounded-2xl p-4 sm:p-5 border border-yellow-400/40 bg-gradient-to-r from-yellow-400/10 via-orange-500/10 to-pink-500/10 hover:from-yellow-400/20 hover:via-orange-500/20 hover:to-pink-500/20 transition-all shadow-lg"
              data-testid={`special-request-${i}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-yellow-400 to-orange-500 text-black shadow">
                {i === 0 ? <Sparkles size={20} /> : <Star size={20} />}
              </div>
              <p className="flex-1 text-sm sm:text-base font-semibold text-white leading-snug">{req}</p>
              <MessageSquare size={16} className="text-yellow-300 flex-shrink-0" />
            </a>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-5" data-testid="song-search-wrapper">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by artist or title..."
            className="w-full pl-11 pr-11 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-colors"
            data-testid="song-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Clear search"
              data-testid="song-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <p className="text-xs text-gray-400 mb-3 px-1" data-testid="song-count">
          Showing <span className="text-purple-300 font-semibold">{filtered.length}</span> of {songs.length} songs
        </p>

        {/* Song list */}
        <ol className="space-y-2" data-testid="song-list">
          {filtered.map(({ song, index }) => (
            <li key={index}>
              <a
                href={smsHref(song)}
                className="group flex items-center gap-4 rounded-xl px-4 py-3.5 bg-white/5 border border-white/10 hover:border-purple-500/40 hover:bg-purple-500/5 active:scale-[0.99] transition-all"
                data-testid={`song-${index}`}
              >
                <span className="w-9 h-9 flex-shrink-0 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-200 text-xs font-bold flex items-center justify-center tabular-nums">
                  {index}
                </span>
                <span className="flex-1 text-sm sm:text-base text-gray-100 leading-snug">{song}</span>
                <MessageSquare
                  size={16}
                  className="text-gray-500 group-hover:text-purple-300 transition-colors flex-shrink-0"
                />
              </a>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="text-center py-10 text-gray-400 text-sm" data-testid="song-empty">
              No matches — try a different word, or shout it out anyway. I&apos;ll learn it in 2 minutes 🎸
            </li>
          )}
        </ol>

        <div className="mt-8 text-center">
          <a
            href={`sms:+1${BOOKING_PHONE.replace(/-/g, '')}?&body=${encodeURIComponent('Song request: ')}`}
            className="inline-flex items-center gap-2 font-bold text-black text-base px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:brightness-110 transition-all shadow-lg"
            data-testid="text-request-button"
          >
            <MessageSquare size={18} />
            Text your own request
          </a>
          <p className="text-xs text-gray-500 mt-2">Opens your messages app to {BOOKING_PHONE}</p>
        </div>
      </main>
    </div>
  );
};

export default SongRequestsPage;
